import { css, SerializedStyles } from '@emotion/core'
import * as React from 'react'

import { longCopyMobileBreakPoint } from '../constants/breakPoints'
import ChevronTopIcon from '../icons/abstract/chevron-top.svg'
import { anchorsMobileNavigationHeight } from './AnchorsMobileNavigation'

interface ScrollToTopProps {
  bgColor?: 'blue' | 'green' | 'purple' | 'peach'
  // if referenceIdForVibility is set, the button will be visible when
  // the element is scrolled out of the viewport to the top
  // otherwise the button will be visible when visitor scrolls > 100vh
  className?: string
  referenceIdForVisibility?: string
  referenceIdForScrollingBlock?: string
  referenceIdToScrollTo?: string
  cssStyles?: SerializedStyles
}

const ScrollToTop: React.FunctionComponent<ScrollToTopProps> = ({
  bgColor,
  className,
  referenceIdForVisibility = '',
  referenceIdForScrollingBlock = '',
  referenceIdToScrollTo,
  cssStyles = css``,
}) => {
  const [isVisible, setIsVisible] = React.useState(false)

  // always double check on gatsby-browser.js if intercom is visible on the page (might overlap)
  const intercomButtonOnPage =
    typeof window !== 'undefined' &&
    (window.location.pathname.includes('/academy/') ||
      window.location.pathname.includes('/resources/events/') ||
      window.location.pathname.includes('/glossary/') ||
      window.location.pathname.includes('/product-learn/') ||
      window.location.pathname.includes('/product-tour/') ||
      window.location.pathname.includes('/try-now/product-template/') ||
      window.location.pathname.includes('/try-now/roadmap-template/') ||
      window.location.pathname.includes('/try-now/event-library/'))

  React.useEffect(() => {
    const referenceElementScrollingBlock = document.getElementById(referenceIdForScrollingBlock)
    if (referenceElementScrollingBlock) {
      // console.log(referenceElementScrollingBlock)
      // console.log(referenceElementScrollingBlock.getBoundingClientRect().top)
      referenceElementScrollingBlock.addEventListener('scroll', handleScroll)
    } else {
      document.addEventListener('scroll', handleScroll)
    }

    return () => {
      if (referenceElementScrollingBlock) {
        referenceElementScrollingBlock.removeEventListener('scroll', handleScroll)
      } else {
        document.removeEventListener('scroll', handleScroll)
      }
    }
  }, [])

  // Function to handle scroll event and toggle button visibility
  const handleScroll = () => {
    const referenceElementForVisibility = document.getElementById(referenceIdForVisibility)
    const footer = document.getElementById('footer')
    if (referenceElementForVisibility) {
      if (referenceElementForVisibility.getBoundingClientRect().top < 0) {
        setIsVisible(true)
      } else {
        setIsVisible(false)
      }
    } else if (window.scrollY > window.innerHeight) {
      setIsVisible(true)
    } else {
      setIsVisible(false)
    }

    // hide button when footer is scrolled into view
    if (footer && footer.getBoundingClientRect().top < window.innerHeight) {
      setIsVisible(false)
    }
  }

  const scrollToTop = () => {
    if (referenceIdToScrollTo) {
      document.getElementById(referenceIdToScrollTo)?.scrollIntoView({ behavior: 'smooth' })
    } else if (referenceIdForScrollingBlock) {
      document.getElementById(referenceIdForScrollingBlock)?.scrollTo({ top: 0, behavior: 'smooth' })
    } else {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }
  }

  const additionalPaddingBottom =
    typeof document !== 'undefined' && document.getElementById('anchors-mobile-container')
      ? anchorsMobileNavigationHeight
      : 0

  return (
    <div
      className={`${bgColor}-gradient-bg with-box-shadow ${!!className && className}`}
      onClick={scrollToTop}
      css={[scrollToTopContainerStyle(isVisible, additionalPaddingBottom, intercomButtonOnPage), cssStyles]}
    >
      <ChevronTopIcon />
    </div>
  )
}

export default ScrollToTop

const scrollToTopContainerStyle = (
  isVisible: boolean,
  additionalPaddingBottom: number,
  intercomButtonOnPage: boolean
) => css`
  transform: translateX(calc(100% + 1.25rem)) ${isVisible ? 'scale(1)' : 'scale(0)'};
  opacity: ${isVisible ? 1 : 0};
  transition: all 0.2s ease-in-out !important;
  position: fixed;
  // intercom button is 20px from the bottom
  bottom: 1.25rem;
  right: max((var(--emptySpaceWidth)), 2rem);
  z-index: 3;
  background-color: #318eff;
  border-radius: 50%;
  width: 3rem;
  height: 3rem;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    cursor: pointer;
    // matches #318eff65 (without opacity)
    background-color: #78aff1;
  }

  ${intercomButtonOnPage
    ? `
      @media screen and (max-width: 1440px) {
        transform: ${isVisible ? 'scale(1)' : 'scale(0)'};
        // bottom: 20px + intercom button height + 20px
        bottom: calc(20px + 3rem + 20px);
        right: 20px;
      }

  `
    : `
      @media screen and (max-width: 1300px) {
        transform: ${isVisible ? 'scale(1)' : 'scale(0)'};
        right: 1.25rem;
      }
      
      @media screen and (max-width: ${longCopyMobileBreakPoint}px) {
        bottom: calc(1.5rem + ${additionalPaddingBottom}px);
      }
  
  `}
`
